import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Field } from "@/components/ui-kit";
import { toast } from "sonner";
import { loadPdfLib } from "@/lib/pdflib";
import { ArrowLeft, CheckCircle2, AlertTriangle, FileDown, Trash2 } from "lucide-react";

// ---- Annual STS coordinate tables, ported verbatim from the compliance portal (admin.html) ----
const AN_SUBJECT_X = { transporting_passenger: 140, operating_radio: 198, ramp_lift_securement: 275, emergency_procedures: 343, daily_inspection: 410, clean_sanitize: 469, driving_eval: 518 };
const AN_45_X = { first_aid: 145, defensive: 185, abuse: 235, pa_ambulatory: 292, pa_wc: 360, pa_stretcher: 447, pa_child: 517 };
const AN_REF_X = { first_aid: 190, abuse: 248, securement: 305, defensive: 373, pa: 438, conted: 508 };
const AN_REF_TOPS = [431, 456, 480, 505, 529, 554, 578, 603, 627, 652];
// The portal's AN_CHK_TOPS (the 12-row annual record-check grid) is not ported: it belongs to a
// separate record-check log (driver_record_checks) that is not part of this build's data model.

const usDate = (s) => {
  if (!s) return "";
  const d = new Date(`${s}T00:00:00`);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const slug = (s) => String(s || "driver").replace(/[^A-Za-z0-9]+/g, "_");

function downloadBytes(bytes, filename) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// The driver record on file may not carry DOB / license state / a signed address yet.
// When a license-prefill endpoint exists, use it; otherwise fall back to the driver record
// and leave the rest blank so the form stays honest about what is and is not on file.
async function fetchLicensePrefill(driverId) {
  try {
    const { data } = await api.get(`/drivers/${driverId}/license-prefill`);
    return data;
  } catch {
    return null;
  }
}

const emptyForm = { category: "initial_subject", course: "", completed_on: "", hours: "", trainer: "", notes: "" };

export default function DriverTraining() {
  const { id } = useParams();
  const [driver, setDriver] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(null);
  const [catalog, setCatalog] = useState({ categories: [], courses: {} });
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busyPdf, setBusyPdf] = useState(null); // "annual" | null
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [driversRes, rowsRes, statusRes, catalogRes] = await Promise.all([
        api.get("/drivers"),
        api.get(`/drivers/${id}/training`),
        api.get(`/drivers/${id}/training/status`),
        api.get("/training/catalog"),
      ]);
      setDriver(driversRes.data.find((d) => d.id === id) || null);
      setRows(rowsRes.data);
      setStatus(statusRes.data);
      setCatalog(catalogRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const courseOptions = useMemo(() => {
    const list = catalog.courses[form.category] || [];
    return list.map((c) => ({ value: c.key, label: c.label }));
  }, [catalog, form.category]);

  const categoryLabel = (key) => (catalog.categories.find((c) => c.key === key) || {}).label || key;
  const courseLabel = (cat, key) => ((catalog.courses[cat] || []).find((c) => c.key === key) || {}).label || key;

  const grouped = useMemo(() => {
    const byCat = {};
    for (const r of rows) {
      if (!byCat[r.category]) byCat[r.category] = [];
      byCat[r.category].push(r);
    }
    return catalog.categories.map((c) => ({ category: c.key, label: c.label, rows: byCat[c.key] || [] })).filter((g) => g.rows.length > 0);
  }, [rows, catalog]);

  const onCategoryChange = (e) => {
    const category = e.target.value;
    const first = (catalog.courses[category] || [])[0];
    setForm({ ...form, category, course: first ? first.key : "" });
  };

  const addTraining = async () => {
    if (!form.course) return toast.error("Choose a course.");
    if (!form.completed_on) return toast.error("Pick the completion date.");
    setSaving(true);
    try {
      await api.post(`/drivers/${id}/training`, {
        category: form.category,
        course: form.course,
        completed_on: form.completed_on,
        hours: form.hours === "" ? null : Number(form.hours),
        trainer: form.trainer || null,
        notes: form.notes || null,
      });
      toast.success("Training record added");
      setForm({ ...emptyForm, category: form.category, course: (catalog.courses[form.category] || [])[0]?.key || "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeTraining = async (tid) => {
    if (!confirm("Delete this training record? This cannot be undone.")) return;
    try {
      await api.delete(`/drivers/training/${tid}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const generateAnnual = async () => {
    setBusyPdf("annual");
    try {
      const PDFLib = await loadPdfLib();
      const res = await fetch("/forms/Annual_STS_Form.pdf");
      if (!res.ok) throw new Error("Could not load the blank Annual STS form. Try again in a moment.");
      const bytes = await res.arrayBuffer();
      const pdf = await PDFLib.PDFDocument.load(bytes);
      const font = await pdf.embedFont(PDFLib.StandardFonts.Helvetica);
      const [p1, p2] = pdf.getPages();
      const H = 792;
      const put = (pg, x, top, text, size = 9) => {
        if (text == null || text === "") return;
        pg.drawText(String(text), { x, y: H - top - size, size, font, color: PDFLib.rgb(0, 0, 0) });
      };

      const prefill = await fetchLicensePrefill(id);
      const name = prefill?.name || driver?.name || "";
      const addr = prefill?.address || "";
      const licenseNumber = prefill?.license_number || driver?.license_number || "";
      const licenseClass = prefill?.license_class || "";
      const dob = prefill?.dob ? usDate(prefill.dob) : "";

      put(p1, 190, 218, name);
      put(p1, 130, 241, addr, 8);
      put(p1, 195, 267, licenseNumber);
      put(p1, 498, 267, licenseClass);
      put(p1, 180, 293, dob);

      const fa = rows.find((r) => r.category === "first_aid_initial");
      if (fa) put(p1, 497, 633, usDate(fa.completed_on), 8);
      const pa = rows.find((r) => r.category === "passenger_assistance_initial");
      if (pa) put(p1, 282, 693, usDate(pa.completed_on), 8);

      rows.filter((r) => r.category === "initial_subject").forEach((r) => {
        const x = AN_SUBJECT_X[r.course];
        if (x) put(p2, x, 149, usDate(r.completed_on), 7);
      });
      rows.filter((r) => r.category === "additional_45day").forEach((r) => {
        const x = AN_45_X[r.course];
        if (x) put(p2, x, 294, usDate(r.completed_on), 7);
      });
      const refRow = {};
      rows.filter((r) => r.category === "refresher").forEach((r) => {
        const x = AN_REF_X[r.course];
        if (x == null) return;
        const ri = refRow[r.course] || 0;
        if (ri < AN_REF_TOPS.length) {
          put(p2, x, AN_REF_TOPS[ri], usDate(r.completed_on) + (r.hours != null ? ` ${r.hours}h` : ""), 6.5);
          refRow[r.course] = ri + 1;
        }
      });

      const out = await pdf.save();
      downloadBytes(out, `Annual_STS_${slug(name)}_${todayISO()}.pdf`);
    } catch (e) {
      toast.error(e.message || "Could not build the Annual STS form.");
    } finally {
      setBusyPdf(null);
    }
  };

  if (loading) return <div className="text-sm text-slate-500 p-4">Loading…</div>;
  if (!driver) return <div className="text-sm text-slate-500 p-4">Driver not found. <Link to="/app/drivers" className="text-[#005E82] hover:underline">Back to Drivers</Link></div>;

  return (
    <div data-testid="driver-training-page">
      <Link to="/app/drivers" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0A192F] mb-3" data-testid="back-to-drivers">
        <ArrowLeft size={13} /> Back to Drivers
      </Link>
      <PageHeader
        subtitle="Drivers · Training"
        title={driver.name}
        action={
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button onClick={generateAnnual} disabled={busyPdf !== null} className="btn-primary text-xs inline-flex items-center justify-center gap-1 min-h-[44px] disabled:opacity-60" data-testid="annual-pdf-btn">
              <FileDown size={13} /> {busyPdf === "annual" ? "Building…" : "Annual STS form (PDF)"}
            </button>
          </div>
        }
      />

      {status && (
        <div
          className={`mb-5 rounded-md border-l-4 p-4 ${status.ok ? "bg-emerald-50 border-emerald-500" : "bg-rose-50 border-rose-500"}`}
          data-testid="gate-banner"
        >
          <div className={`flex items-center gap-2 font-semibold ${status.ok ? "text-emerald-700" : "text-rose-700"}`}>
            {status.ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            {status.summary}
          </div>
          {!status.ok && status.missing.length > 0 && (
            <ul className="mt-2 ml-6 list-disc text-sm text-slate-700 space-y-1">
              {status.missing.map((m) => (
                <li key={`${m.kind}-${m.key}`}>
                  {m.label}{" "}
                  <span className="text-slate-400 text-xs">({m.kind === "training" ? "training" : "document"})</span>{" "}
                  {m.kind === "training" ? (
                    <a href="#add-training-form" className="text-[#005E82] hover:underline text-xs">Add now</a>
                  ) : (
                    <Link to="/app/compliance" className="text-[#005E82] hover:underline text-xs">Manage documents</Link>
                  )}
                </li>
              ))}
            </ul>
          )}
          {status.refresher_due_on && (
            <div className={`mt-2 text-xs ${status.refresher_overdue ? "text-rose-700 font-semibold" : "text-slate-600"}`}>
              Refresher {status.refresher_overdue ? "was due" : "due"} {status.refresher_due_on}
              {status.refresher_overdue ? ", overdue." : "."}
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-5">
        <div className="font-display font-semibold text-slate-900 mb-3">Training log</div>
        {grouped.length === 0 && <div className="empty">No training recorded yet.</div>}
        {grouped.map((g) => (
          <div key={g.category} className="mb-4 last:mb-0">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-2">{g.label}</div>

            {/* table at sm and up */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-[0.1em] text-slate-500 bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2">Course</th>
                    <th className="text-left px-3 py-2">Completed on</th>
                    <th className="text-left px-3 py-2">Hours</th>
                    <th className="text-left px-3 py-2">Trainer</th>
                    <th className="text-left px-3 py-2">Notes</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100" data-testid={`training-row-${r.id}`}>
                      <td className="px-3 py-2">{courseLabel(g.category, r.course)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.completed_on}</td>
                      <td className="px-3 py-2">{r.hours != null ? r.hours : "-"}</td>
                      <td className="px-3 py-2">{r.trainer || "-"}</td>
                      <td className="px-3 py-2 text-slate-600">{r.notes || "-"}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => removeTraining(r.id)} className="text-slate-400 hover:text-rose-600" data-testid={`delete-training-${r.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* cards under 640px */}
            <div className="sm:hidden space-y-2">
              {g.rows.map((r) => (
                <div key={r.id} className="border border-slate-200 rounded-md p-3" data-testid={`training-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-sm">{courseLabel(g.category, r.course)}</div>
                    <button onClick={() => removeTraining(r.id)} className="text-slate-400 hover:text-rose-600 min-h-[44px] min-w-[44px] flex items-center justify-center -m-2">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 font-mono">{r.completed_on}{r.hours != null ? ` · ${r.hours}h` : ""}</div>
                  {r.trainer && <div className="text-xs text-slate-500 mt-0.5">Trainer: {r.trainer}</div>}
                  {r.notes && <div className="text-xs text-slate-500 mt-0.5">{r.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4" id="add-training-form">
        <div className="font-display font-semibold text-slate-900 mb-3">Add training record</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label="Category"
            testId="tr-category"
            value={form.category}
            onChange={onCategoryChange}
            options={catalog.categories.map((c) => ({ value: c.key, label: c.label }))}
          />
          <Field
            label="Course"
            testId="tr-course"
            value={form.course}
            onChange={(e) => setForm({ ...form, course: e.target.value })}
            options={courseOptions}
          />
          <Field label="Completed on" testId="tr-date" type="date" value={form.completed_on} onChange={(e) => setForm({ ...form, completed_on: e.target.value })} />
          <Field label="Hours" testId="tr-hours" type="number" min="0" step="0.25" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} />
          <Field label="Trainer" testId="tr-trainer" value={form.trainer} onChange={(e) => setForm({ ...form, trainer: e.target.value })} />
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-200"
              placeholder="Optional"
            />
          </div>
        </div>
        <div className="mt-4">
          <button onClick={addTraining} disabled={saving} className="btn-primary min-h-[44px] disabled:opacity-60" data-testid="add-training-btn">
            {saving ? "Adding…" : "Add training record"}
          </button>
        </div>
      </div>
    </div>
  );
}
